import React, { useState, useEffect } from 'react';
import { api, type FlyerMatchCandidate, type RejectedEvent } from '../../services/api';
import { EventCard } from '../common/EventCard';
import type { Event } from '../../types';
import { useLang } from '../../i18n/context';

interface ValidationTabProps {
  onUpdate: () => void;
}

interface DupePair {
  id1: string; title1: string; date: string; city: string; venue_id1: string | null;
  id2: string; title2: string; venue_id2: string | null; similarity: number;
}

// ── Inline edit form for pending events ──
function EditPanel({ event, onSave, onCancel }: { event: Event; onSave: (data: any) => Promise<void>; onCancel: () => void }) {
  const { t } = useLang();
  const [form, setForm] = useState({
    title: event.title || '',
    date: event.date || '',
    city: event.city || event.venue_city || '',
    price_info: event.price_info || '',
    is_free: event.is_free || false,
    start_time: event.start_time || '',
    end_time: event.end_time || '',
    description: event.description || '',
  });
  const [saving, setSaving] = useState(false);

  const fieldStyle: React.CSSProperties = {
    width: '100%', padding: '6px 8px', borderRadius: '4px',
    border: '1px solid var(--border-color)', background: 'var(--bg-color)',
    color: 'var(--text-color)', fontSize: '12px', fontFamily: 'inherit',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase',
    letterSpacing: '0.5px', marginBottom: '3px',
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSave({
        title: form.title || undefined,
        date: form.date || undefined,
        city: form.city || undefined,
        price_info: form.price_info || undefined,
        is_free: form.is_free,
        start_time: form.start_time || undefined,
        end_time: form.end_time || undefined,
        description: form.description || undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderTop: '1px solid var(--border-color)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
        <div>
          <div style={labelStyle}>Title</div>
          <input style={fieldStyle} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
        </div>
        <div>
          <div style={labelStyle}>Date</div>
          <input type="date" style={fieldStyle} value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
        </div>
        <div>
          <div style={labelStyle}>City</div>
          <input style={fieldStyle} value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
        </div>
        <div>
          <div style={labelStyle}>Price</div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <input style={{ ...fieldStyle, flex: 1 }} value={form.price_info} onChange={e => setForm(f => ({ ...f, price_info: e.target.value }))}
              placeholder="e.g. 10€" disabled={form.is_free} />
            <label style={{ fontSize: '11px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '3px' }}>
              <input type="checkbox" checked={form.is_free} onChange={e => setForm(f => ({ ...f, is_free: e.target.checked }))} /> FREE
            </label>
          </div>
        </div>
        <div>
          <div style={labelStyle}>Start time</div>
          <input type="time" style={fieldStyle} value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} />
        </div>
        <div>
          <div style={labelStyle}>End time</div>
          <input type="time" style={fieldStyle} value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} />
        </div>
      </div>
      <div style={{ marginBottom: '8px' }}>
        <div style={labelStyle}>Description / raw OCR</div>
        <textarea style={{ ...fieldStyle, minHeight: '60px', resize: 'vertical' }} value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={saving}>
          {saving ? '...' : '✓ Save'}
        </button>
        <button className="btn btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export function ValidationTab({ onUpdate }: ValidationTabProps) {
  const { t, lang } = useLang();
  const [view, setView] = useState<'pending' | 'rejected'>('pending');
  const [events, setEvents] = useState<Event[]>([]);
  const [dupes, setDupes] = useState<DupePair[]>([]);
  const [flyerMatches, setFlyerMatches] = useState<FlyerMatchCandidate[]>([]);
  const [rejected, setRejected] = useState<RejectedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => { loadAll(); }, [lang]);

  async function loadAll() {
    setLoading(true);
    try {
      const [data, suspected, matches, rej] = await Promise.all([
        api.getEvents({ status: 'pending' }),
        api.getSuspectedDuplicates(),
        api.getFlyerMatches().catch(() => [] as FlyerMatchCandidate[]),
        api.getRejectedEvents().catch(() => [] as RejectedEvent[]),
      ]);
      setEvents(data);
      setDupes(suspected);
      setFlyerMatches(matches);
      setRejected(rej);
    } catch (err) {
      console.error('Failed to load validation data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handlePermanentDelete(id: string) {
    if (!window.confirm(t('validation.confirm_permanent_delete'))) return;
    try {
      await api.permanentDeleteRejected(id);
      setRejected(prev => prev.filter(r => r.id !== id));
      onUpdate();
    } catch (err) {
      console.error('Failed to permanently delete:', err);
    }
  }

  async function handleFlyerMerge(id: number, keep: 'a' | 'b' = 'a') {
    try {
      await api.mergeFlyerMatch(id, keep);
      setFlyerMatches(prev => prev.filter(m => m.id !== id));
      onUpdate();
    } catch (err) {
      console.error('Failed to merge flyer match:', err);
    }
  }

  // Close = defer this pair without a decision (no API call — reappears next load).
  function handleFlyerClose(id: number) {
    setFlyerMatches(prev => prev.filter(m => m.id !== id));
  }

  async function handleFlyerDismiss(id: number) {
    try {
      await api.dismissFlyerMatch(id);
      setFlyerMatches(prev => prev.filter(m => m.id !== id));
    } catch (err) {
      console.error('Failed to dismiss flyer match:', err);
    }
  }

  async function handleValidate(id: string) {
    try {
      await api.validateEvent(id);
      setEvents(prev => prev.filter(e => e.id !== id));
      onUpdate();
    } catch (err) {
      console.error('Failed to validate:', err);
    }
  }

  async function handleReject(id: string) {
    try {
      await api.rejectEvent(id);
      setEvents(prev => prev.filter(e => e.id !== id));
      onUpdate();
    } catch (err) {
      console.error('Failed to reject:', err);
    }
  }

  async function handleEdit(id: string, data: any) {
    await api.updateEvent(id, data);
    setEditingId(null);
    await loadAll();
    onUpdate();
  }

  async function handleValidateAll() {
    const validatedIds: string[] = [];
    const failures: { id: string; err: unknown }[] = [];
    for (const event of events) {
      try {
        await api.validateEvent(event.id);
        validatedIds.push(event.id);
      } catch (err) {
        failures.push({ id: event.id, err });
        console.error('Failed to validate event', event.id, err);
      }
    }
    if (validatedIds.length > 0) {
      const validated = new Set(validatedIds);
      setEvents(prev => prev.filter(e => !validated.has(e.id)));
      onUpdate();
    }
    if (failures.length > 0) {
      window.alert(`${failures.length} / ${events.length} ${t('validation.validate_all_partial_failure') || 'failed to validate — see console'}`);
    }
  }

  const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-BE';

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

  if (loading) return <div className="loading"><span className="spinner"></span> {t('common.loading')}</div>;

  const pillStyle = (active: boolean): React.CSSProperties => ({
    padding: '5px 12px', fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px',
    borderRadius: '4px', border: '1px solid var(--border-color)',
    background: active ? 'rgba(76,175,80,0.15)' : 'transparent',
    color: active ? 'var(--green)' : 'var(--text-muted)',
    cursor: 'pointer', textTransform: 'uppercase',
  });

  // Group rejected events by validation_reason for systematic-bug analysis
  const rejectedGrouped = rejected.reduce<Record<string, RejectedEvent[]>>((acc, r) => {
    const key = r.validation_reason || '(no reason recorded)';
    (acc[key] = acc[key] || []).push(r);
    return acc;
  }, {});

  return (
    <div>
      {/* Pending / Rejected view toggle */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
        <button style={pillStyle(view === 'pending')} onClick={() => setView('pending')}>
          {t('validation.view_pending')} ({events.length})
        </button>
        <button style={pillStyle(view === 'rejected')} onClick={() => setView('rejected')}>
          {t('validation.view_rejected')} ({rejected.length})
        </button>
      </div>

      {view === 'rejected' && (
        <div>
          <div className="section-header">
            <span className="section-title">{t('validation.rejected_title')}</span>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              {t('validation.rejected_hint')}
            </span>
          </div>

          {rejected.length === 0 ? (
            <div className="empty-state"><p>{t('validation.no_rejected')}</p></div>
          ) : (
            Object.entries(rejectedGrouped)
              .sort(([, a], [, b]) => b.length - a.length)
              .map(([reason, group]) => (
                <div key={reason} style={{ marginBottom: '20px' }}>
                  <div style={{
                    padding: '6px 10px', marginBottom: '8px',
                    background: 'rgba(201,168,76,0.10)', borderLeft: '3px solid var(--gold)',
                    fontSize: '11px', fontFamily: 'monospace', color: 'var(--gold)',
                    display: 'flex', justifyContent: 'space-between',
                  }}>
                    <span>⚠ {reason}</span>
                    <span style={{ opacity: 0.7 }}>{group.length} {group.length === 1 ? 'event' : 'events'}</span>
                  </div>
                  <div className="event-grid">
                    {group.map(r => {
                      const eventForCard: Event = {
                        id: r.id,
                        title: r.title,
                        date: r.date,
                        city: r.city,
                        image_url: r.image_url,
                        source_url: r.source_url,
                        description: r.description,
                        is_free: !!r.is_free,
                        price_info: r.price_info,
                        start_time: r.start_time,
                        end_time: r.end_time,
                        venue_name: r.venue_name,
                        venue_city: r.venue_city,
                        venue_address: r.venue_address,
                        status: 'rejected',
                        genre_tags: [],
                        artists: [],
                        created_at: r.created_at,
                        updated_at: r.updated_at || r.created_at,
                      };
                      return (
                        <div key={r.id}>
                          <EventCard event={eventForCard} showActions={false} forceExpanded={expandedId === r.id} />
                          <div style={{
                            marginTop: '-1px', padding: '8px 12px', borderRadius: '0 0 8px 8px',
                            background: 'var(--card-bg)', borderTop: '1px solid var(--border-color)',
                            display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center',
                          }}>
                            <button className="btn btn-danger btn-sm" onClick={() => handlePermanentDelete(r.id)}>
                              🗑 {t('validation.permanent_delete')}
                            </button>
                            <button className="btn btn-sm" onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                              style={expandedId === r.id ? { borderColor: 'var(--gold)', color: 'var(--gold)' } : {}}>
                              {expandedId === r.id ? `▲ ${t('validation.collapse')}` : `▼ ${t('validation.expand')}`}
                            </button>
                            {r.source_url && (
                              <a href={r.source_url} target="_blank" rel="noopener noreferrer"
                                className="btn btn-sm"
                                style={{ textDecoration: 'none', marginLeft: 'auto', opacity: 0.85 }}
                                title={r.source_url}>
                                🔗 {t('validation.view_source')}
                              </a>
                            )}
                            {r.source_name && (
                              <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                                src: {r.source_name}{r.source_type ? ` (${r.source_type})` : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
          )}
        </div>
      )}

      {view === 'pending' && <>
      <div className="section-header">
        <span className="section-title">{t('validation.title')}</span>
        {events.length > 0 && (
          <button className="btn btn-success" onClick={handleValidateAll}>
            {t('validation.validate_all')} ({events.length})
          </button>
        )}
      </div>

      {events.length === 0 ? (
        <div className="empty-state">
          <p>{t('validation.no_pending')}</p>
        </div>
      ) : (
        <div className="event-grid">
          {events.map(event => (
            <div key={event.id}>
              <EventCard event={event} showActions={false} forceExpanded={expandedId === event.id} />
              {event.validation_reason && (
                <div style={{
                  marginTop: '-1px', padding: '6px 12px',
                  background: 'rgba(201,168,76,0.10)', borderTop: '1px solid var(--border-color)',
                  fontSize: '11px', color: 'var(--gold)', fontFamily: 'monospace',
                }}>
                  ⚠ {event.validation_reason}
                </div>
              )}
              <div className="validation-actions" style={{ marginTop: '-1px', padding: '8px 12px', borderRadius: editingId === event.id ? '0' : '0 0 8px 8px', background: 'var(--card-bg)', borderTop: '1px solid var(--border-color)', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                <button className="btn btn-success btn-sm" onClick={() => handleValidate(event.id)}>
                  &#10003; {t('validation.validate')}
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => handleReject(event.id)}>
                  &#10007; {t('validation.reject')}
                </button>
                <button className="btn btn-sm" onClick={() => setEditingId(editingId === event.id ? null : event.id)}
                  style={editingId === event.id ? { borderColor: 'var(--orange)', color: 'var(--orange)' } : {}}>
                  &#9998; {t('validation.edit')}
                </button>
                <button className="btn btn-sm" onClick={() => setExpandedId(expandedId === event.id ? null : event.id)}
                  style={expandedId === event.id ? { borderColor: 'var(--gold)', color: 'var(--gold)' } : {}}>
                  {expandedId === event.id ? `▲ ${t('validation.collapse')}` : `▼ ${t('validation.expand')}`}
                </button>
                {event.source_url && (
                  <a
                    href={event.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm"
                    style={{ textDecoration: 'none', marginLeft: 'auto', opacity: 0.85 }}
                    title={event.source_url}
                  >
                    🔗 {t('validation.view_source')}
                  </a>
                )}
              </div>
              {editingId === event.id && (
                <div style={{ borderRadius: '0 0 8px 8px', overflow: 'hidden', background: 'var(--card-bg)' }}>
                  <EditPanel event={event} onSave={(data) => handleEdit(event.id, data)} onCancel={() => setEditingId(null)} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Suspected duplicates ─────────────────────────────────────── */}
      {dupes.length > 0 && (
        <div style={{ marginTop: '32px' }}>
          <div className="section-header" style={{ marginBottom: '12px' }}>
            <span className="section-title" style={{ fontSize: '14px' }}>
              {t('validation.suspected_dupes')}
            </span>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              {t('validation.pairs').replace('{n}', String(dupes.length))}
            </span>
          </div>

          {dupes.map((d, i) => (
            <div key={i} className="validation-card" style={{ marginBottom: '10px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                <span>{new Date(d.date).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{d.city}</span>
                <span style={{
                  padding: '1px 6px', borderRadius: '4px', fontWeight: 700,
                  background: d.similarity >= 60 ? 'rgba(229,57,53,0.15)' : 'rgba(201,168,76,0.12)',
                  color: d.similarity >= 60 ? 'var(--red)' : 'var(--gold)',
                }}>
                  {d.similarity}% {t('validation.similarity')}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <div style={{ padding: '8px', borderRadius: '4px', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-color)' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '2px' }}>{d.title1}</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{d.venue_id1 ? t('validation.venue_known') : t('validation.venue_unknown')}</div>
                </div>
                <div style={{ padding: '8px', borderRadius: '4px', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-color)' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '2px' }}>{d.title2}</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{d.venue_id2 ? t('validation.venue_known') : t('validation.venue_unknown')}</div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-danger btn-sm" onClick={async () => {
                  await api.rejectEvent(d.id2);
                  setDupes(prev => prev.filter((_, j) => j !== i));
                  onUpdate();
                }}>
                  {t('validation.keep_first')}
                </button>
                <button className="btn btn-danger btn-sm" onClick={async () => {
                  await api.rejectEvent(d.id1);
                  setDupes(prev => prev.filter((_, j) => j !== i));
                  onUpdate();
                }}>
                  {t('validation.keep_second')}
                </button>
                <button className="btn btn-sm" onClick={() => setDupes(prev => prev.filter((_, j) => j !== i))}>
                  {t('validation.ignore')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Flyer matches (pHash-detected duplicate flyers) ───────────── */}
      {flyerMatches.length > 0 && (
        <div style={{ marginTop: '32px' }}>
          <div className="section-header" style={{ marginBottom: '12px' }}>
            <span className="section-title" style={{ fontSize: '14px' }}>
              {t('validation.flyer_matches')}
            </span>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              {flyerMatches.length} {flyerMatches.length === 1 ? 'pair' : 'pairs'}
            </span>
          </div>

          {flyerMatches.map(m => {
            const dateA = new Date(m.a_date).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
            const dateB = new Date(m.b_date).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
            const badgeColor = m.hamming_distance <= 4
              ? { bg: 'rgba(229,57,53,0.15)', fg: 'var(--red)' }     // very likely same
              : m.hamming_distance <= 8
                ? { bg: 'rgba(201,168,76,0.15)', fg: 'var(--gold)' } // likely same
                : { bg: 'rgba(200,200,200,0.10)', fg: 'var(--text-muted)' };  // possibly same

            const matchKey = `flyer-${m.id}`;
            const isExpanded = expandedId === matchKey;
            const imgDim = isExpanded ? '100%' : '80px';
            const imgStyle: React.CSSProperties = isExpanded
              ? { width: '100%', maxHeight: '480px', objectFit: 'contain', borderRadius: '4px', display: 'block' }
              : { width: '80px', height: '80px', objectFit: 'cover', borderRadius: '3px', flexShrink: 0 };
            const placeholderStyle: React.CSSProperties = isExpanded
              ? { width: '100%', height: '200px', borderRadius: '4px', background: 'rgba(255,255,255,0.03)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: 'var(--text-muted)' }
              : { width: '80px', height: '80px', borderRadius: '3px', background: 'rgba(255,255,255,0.03)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: 'var(--text-muted)' };

            const card = (img?: string, title?: string, date?: string, city?: string, venue?: string, source?: string, status?: string, srcUrl?: string) => (
              <div style={{ padding: '8px', borderRadius: '4px', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-color)', display: 'flex', flexDirection: isExpanded ? 'column' : 'row', gap: '8px' }}>
                {img ? (
                  <img src={img} alt="" style={imgStyle} />
                ) : (
                  <div style={placeholderStyle}>no flyer</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, whiteSpace: isExpanded ? 'normal' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>{date} — {city || '?'}</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{venue || '—'}</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    <span style={{ opacity: 0.7 }}>src:</span> {source || '?'} · <span style={{ opacity: 0.7 }}>status:</span> {status}
                  </div>
                  {isExpanded && srcUrl && (
                    <a href={srcUrl} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: '10px', color: 'var(--gold)', marginTop: '4px', display: 'inline-block' }}>
                      🔗 {t('validation.view_source')}
                    </a>
                  )}
                </div>
              </div>
            );

            // Source URLs aren't joined by the API — build from event IDs when they're IG-style
            const srcUrlA = m.event_a_id.startsWith('ig_event_')
              ? `https://www.instagram.com/p/${m.event_a_id.split('_').pop()}/` : undefined;
            const srcUrlB = m.event_b_id.startsWith('ig_event_')
              ? `https://www.instagram.com/p/${m.event_b_id.split('_').pop()}/` : undefined;

            return (
              <div key={m.id} className="validation-card" style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ padding: '1px 6px', borderRadius: '4px', fontWeight: 700, background: badgeColor.bg, color: badgeColor.fg }}>
                    Hamming {m.hamming_distance}/64
                  </span>
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {m.hamming_distance <= 4 ? 'almost certainly same flyer'
                      : m.hamming_distance <= 8 ? 'likely same flyer'
                      : 'possibly same flyer'}
                  </span>
                  <button className="btn btn-sm" style={{ marginLeft: 'auto', ...(isExpanded ? { borderColor: 'var(--gold)', color: 'var(--gold)' } : {}) }}
                    onClick={() => setExpandedId(isExpanded ? null : matchKey)}>
                    {isExpanded ? `▲ ${t('validation.collapse')}` : `▼ ${t('validation.expand')}`}
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                  {card(m.a_image, m.a_title, dateA, m.a_city, m.a_venue_name, m.a_source_name, m.a_status, srcUrlA)}
                  {card(m.b_image, m.b_title, dateB, m.b_city, m.b_venue_name, m.b_source_name, m.b_status, srcUrlB)}
                </div>

                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <button className="btn btn-danger btn-sm" onClick={() => handleFlyerMerge(m.id, 'a')} title={`Keep left, drop right (${m.event_b_id})`}>
                    ✓ {t('validation.merge_keep_left')}
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleFlyerMerge(m.id, 'b')} title={`Keep right, drop left (${m.event_a_id})`}>
                    ✓ {t('validation.merge_keep_right')}
                  </button>
                  <button className="btn btn-sm" onClick={() => handleFlyerDismiss(m.id)}>
                    ✗ {t('validation.not_duplicate')}
                  </button>
                  <button className="btn btn-sm" onClick={() => handleFlyerClose(m.id)} style={{ marginLeft: 'auto', opacity: 0.7 }}>
                    ✕ {t('validation.close')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </>}
    </div>
  );
}
